import React, { useEffect, useState, useCallback } from 'react';
import { Formik, Form } from 'formik';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Autocomplete,
  InputAdornment,
  Button,
  Link,
  Backdrop,
  CircularProgress,
} from '@mui/material';
import { keyframes } from '@mui/system';
import { MailIcon, ClinicIcon, LockIcon } from './authIcons';
import { capitalizeFirstLetterOfEachWord } from '../../Utils/utils';
import { forgotPasswordInitialValues, forgotPasswordValidationSchema } from './helper/Schema';
import { useNavigate } from 'react-router-dom';
import { AppDispatch, RootState } from '../../store/store';
import { useDispatch, useSelector } from 'react-redux';
import { forgotPasswordApi } from '../../rtk-apis/auth/forgotPassword';
import { clearState } from '../../store/slices/Auth/forgotPassword';
import { multipleHospitalsApi } from '../../rtk-apis/auth/multipleHospitals';
import { Failed } from '../../components';

// ── Theme tokens (shared look with Login) ─────────────────────────────────
const ekg = keyframes`to { stroke-dashoffset: 0; }`;
const drawRing = keyframes`to { stroke-dashoffset: 0; }`;
const drawCheck = keyframes`to { stroke-dashoffset: 0; }`;
const fieldSx = {
  mb: 1.75,
  '& .MuiOutlinedInput-root': {
    backgroundColor: '#0b1726',
    borderRadius: '12px',
    color: '#e7eef7',
    '& fieldset': { borderColor: '#243652' },
    '&:hover fieldset': { borderColor: '#314a6b' },
    '&.Mui-focused fieldset': {
      borderColor: '#2dd4bf',
      boxShadow: '0 0 0 3px rgba(45,212,191,.15)',
    },
  },
  '& .MuiInputBase-input': { color: '#e7eef7' },
  '& input::placeholder': { color: '#6a7e9c', opacity: 1 },
  '& .MuiFormHelperText-root': { color: '#fb7185', marginLeft: '2px' },
  '& svg': { color: '#6a7e9c' },
};
const btnSx = {
  mt: 1,
  height: 50,
  borderRadius: '12px',
  textTransform: 'none',
  fontSize: 15,
  fontWeight: 700,
  color: '#04221d',
  background: 'linear-gradient(90deg,#14b8a6,#2dd4bf)',
  boxShadow: '0 8px 24px -8px rgba(45,212,191,.6)',
  '&:hover': { background: 'linear-gradient(90deg,#0f9e8e,#26c2ad)' },
};
const linkSx = {
  color: '#2dd4bf',
  fontSize: 13,
  textDecoration: 'none',
  fontFamily: 'inherit',
  cursor: 'pointer',
  display: 'inline-flex',
  alignItems: 'center',
  gap: 0.5,
  '&:hover': { color: '#5eead4', textDecoration: 'underline' },
};

const useDebounce = (callback: any, delay: number) => {
  const [timeoutId, setTimeoutId] = useState<any>(null);
  const debounced = useCallback(
    (...args: any[]) => {
      if (timeoutId) clearTimeout(timeoutId);
      const id = setTimeout(() => callback(...args), delay);
      setTimeoutId(id);
    },
    [callback, delay, timeoutId]
  );
  useEffect(
    () => () => {
      if (timeoutId) clearTimeout(timeoutId);
    },
    [timeoutId]
  );
  return debounced;
};

const ForgotPasswordV2 = () => {
  const navigate = useNavigate();
  const dispatch: AppDispatch = useDispatch();
  const { status, result, loading } = useSelector(
    (state: RootState) => state.forgotPasswordResponse
  );
  const multipleHospitalsResponse = useSelector(
    (state: RootState) => state.multipleHospitalsResponse
  );
  const hospitalsWithRoles =
    multipleHospitalsResponse?.hospitals?.map(h => ({ ...h, userRole: h.userRole })) || [];
  const [showSuccess, setShowSuccess] = useState(false);
  const [showFailed, setShowFailed] = useState(false);
  const [selectedHospital, setSelectedHospital] = useState('');

  const debouncedFetchHospitals = useDebounce((email: string) => {
    if (email) dispatch(multipleHospitalsApi({ email: email.toLowerCase() }));
  }, 1000);

  useEffect(() => {
    if (multipleHospitalsResponse?.hospitals?.length === 1 && !multipleHospitalsResponse?.loading) {
      setSelectedHospital(multipleHospitalsResponse.hospitals[0]._id);
    } else {
      setSelectedHospital('');
    }
  }, [multipleHospitalsResponse]);

  const handleSubmit = (values: { email: string }) => {
    dispatch(
      forgotPasswordApi({
        email: values.email,
        ...(selectedHospital && { hospitalId: selectedHospital }),
      })
    );
  };

  useEffect(() => {
    if (status === 'SUCCESS') {
      setShowSuccess(true);
      setTimeout(() => {
        setShowSuccess(false);
        navigate('/otpVerification', {
          state: { email: result?.result?.email, hospitalId: selectedHospital },
        });
      }, 1500);
    }
    if (status === 'ERROR') setShowFailed(true);
    dispatch(clearState());
  }, [status, selectedHospital, navigate, result]);

  return (
    <>
      <Box
        sx={{
          minHeight: '100dvh',
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          p: 3,
          fontFamily: "'Hanken Grotesk', system-ui, sans-serif",
          background: 'radial-gradient(120% 100% at 0% 0%, #0e2740 0%, #03101d 55%, #020a13 100%)',
        }}
      >
        <Paper
          elevation={0}
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '1.05fr 1fr' },
            width: '100%',
            maxWidth: 960,
            overflow: 'hidden',
            borderRadius: '22px',
            backgroundColor: '#0b1726',
            border: '1px solid #243652',
            boxShadow: '0 30px 80px -30px rgba(0,0,0,.75)',
          }}
        >
          {/* Brand panel (shared with Login) */}
          <Box
            sx={{
              position: 'relative',
              display: { xs: 'none', md: 'flex' },
              flexDirection: 'column',
              justifyContent: 'flex-start',
              gap: 2,
              p: '40px 36px',
              color: '#e7eef7',
              overflow: 'hidden',
              background:
                'radial-gradient(130% 90% at 0% 0%, #16314e 0%, #0a1828 58%, #06101d 100%)',
            }}
          >
            <Box
              sx={{
                position: 'relative',
                zIndex: 2,
                display: 'flex',
                alignItems: 'center',
                gap: 1.25,
                fontSize: 20,
                fontWeight: 700,
              }}
            >
              <Box
                component="span"
                sx={{
                  color: '#fb7185',
                  fontSize: 22,
                  filter: 'drop-shadow(0 0 10px rgba(251,113,133,.5))',
                }}
              >
                ❤
              </Box>
              <span>
                Cardiac
                <Box
                  component="b"
                  sx={{ color: '#2dd4bf' }}
                >
                  Health
                </Box>
              </span>
            </Box>
            <Box
              component="svg"
              viewBox="0 0 600 120"
              preserveAspectRatio="none"
              aria-hidden="true"
              sx={{
                position: 'relative',
                width: '100%',
                height: 92,
                mt: 1,
                mb: 0.5,
                zIndex: 1,
                opacity: 0.95,
              }}
            >
              <polyline
                points="0,60 180,60 210,60 224,28 244,96 262,60 360,60 384,44 404,80 424,60 600,60"
                fill="none"
                stroke="rgba(45,212,191,.16)"
                strokeWidth={2}
              />
              <Box
                component="polyline"
                points="0,60 180,60 210,60 224,28 244,96 262,60 360,60 384,44 404,80 424,60 600,60"
                fill="none"
                stroke="#2dd4bf"
                strokeWidth={2.5}
                sx={{
                  filter: 'drop-shadow(0 0 6px rgba(45,212,191,.6))',
                  strokeDasharray: '240 1400',
                  strokeDashoffset: 1640,
                  animation: `${ekg} 3.4s linear infinite`,
                }}
              />
            </Box>
            <Box sx={{ position: 'relative', zIndex: 2 }}>
              <Typography
                component="h2"
                sx={{
                  fontFamily: "'Spectral',serif",
                  fontSize: 30,
                  lineHeight: 1.16,
                  fontWeight: 600,
                  mb: 2.25,
                }}
              >
                Reset your
                <br />
                access securely.
              </Typography>
              <Box
                component="ul"
                sx={{
                  listStyle: 'none',
                  p: 0,
                  m: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 1.25,
                }}
              >
                {[
                  'We email a one-time verification code',
                  'Code expires shortly for safety',
                  'Your reports stay protected',
                ].map(t => (
                  <Box
                    component="li"
                    key={t}
                    sx={{
                      position: 'relative',
                      pl: '20px',
                      color: '#9fb2cc',
                      fontSize: 13.5,
                      '&::before': {
                        content: '""',
                        position: 'absolute',
                        left: 0,
                        top: '7px',
                        width: 7,
                        height: 7,
                        borderRadius: '50%',
                        backgroundColor: '#2dd4bf',
                        boxShadow: '0 0 8px rgba(45,212,191,.7)',
                      },
                    }}
                  >
                    {t}
                  </Box>
                ))}
              </Box>
            </Box>
          </Box>

          {/* Form panel */}
          <Box
            sx={{
              backgroundColor: '#0e1c2e',
              p: { xs: '32px 26px', md: '44px 40px' },
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
            }}
          >
            <Box sx={{ mb: 3 }}>
              <Box
                sx={{
                  width: 46,
                  height: 46,
                  borderRadius: '12px',
                  display: 'grid',
                  placeItems: 'center',
                  color: '#2dd4bf',
                  backgroundColor: 'rgba(45,212,191,.13)',
                  border: '1px solid #2dd4bf',
                  mb: 1.5,
                }}
              >
                <LockIcon size={22} />
              </Box>
              <Typography
                component="h1"
                sx={{
                  fontFamily: "'Spectral',serif",
                  fontSize: 27,
                  fontWeight: 600,
                  color: '#e7eef7',
                }}
              >
                Forgot password
              </Typography>
              <Typography sx={{ fontSize: 13, color: '#6a7e9c', mt: 0.5 }}>
                Enter your email and we’ll send a verification code.
              </Typography>
            </Box>

            <Formik
              initialValues={forgotPasswordInitialValues}
              validationSchema={forgotPasswordValidationSchema}
              onSubmit={handleSubmit}
            >
              {({ errors, touched, values, handleChange }) => (
                <Form>
                  <TextField
                    fullWidth
                    type="email"
                    name="email"
                    placeholder="Email address"
                    value={values.email}
                    onChange={e => {
                      debouncedFetchHospitals(e.target.value);
                      handleChange(e);
                    }}
                    error={!!(touched.email && errors.email)}
                    helperText={touched.email && (errors.email as string)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <MailIcon />
                        </InputAdornment>
                      ),
                    }}
                    sx={fieldSx}
                  />

                  {values.email && multipleHospitalsResponse?.hospitals?.length > 0 && (
                    <Autocomplete
                      fullWidth
                      autoHighlight
                      options={hospitalsWithRoles}
                      getOptionLabel={o => capitalizeFirstLetterOfEachWord(o?.name || '')}
                      isOptionEqualToValue={(o, v) => o._id === v._id}
                      value={hospitalsWithRoles.find(h => h._id === selectedHospital) || null}
                      onChange={(_e, v) => setSelectedHospital(v?._id || '')}
                      loading={multipleHospitalsResponse?.loading}
                      noOptionsText="No clinics found"
                      sx={{
                        '& .MuiAutocomplete-popupIndicator': { color: '#6a7e9c' },
                        '& .MuiAutocomplete-clearIndicator': { color: '#6a7e9c' },
                      }}
                      slotProps={{
                        paper: {
                          sx: {
                            backgroundColor: '#0e1c2e',
                            color: '#e7eef7',
                            border: '1px solid #243652',
                            borderRadius: '12px',
                            mt: 0.5,
                          },
                        },
                        listbox: {
                          sx: {
                            '& .MuiAutocomplete-option': { fontSize: 14, py: 1 },
                            "& .MuiAutocomplete-option[aria-selected='true']": {
                              backgroundColor: 'rgba(45,212,191,.18)',
                            },
                            '& .MuiAutocomplete-option.Mui-focused': {
                              backgroundColor: 'rgba(45,212,191,.14)',
                            },
                          },
                        },
                      }}
                      renderInput={params => (
                        <TextField
                          {...params}
                          name="hospital"
                          placeholder="Search for your clinic…"
                          InputProps={{
                            ...params.InputProps,
                            startAdornment: (
                              <>
                                <InputAdornment position="start">
                                  <ClinicIcon />
                                </InputAdornment>
                                {params.InputProps.startAdornment}
                              </>
                            ),
                            endAdornment: (
                              <>
                                {multipleHospitalsResponse?.loading ? (
                                  <CircularProgress
                                    size={16}
                                    sx={{ color: '#6a7e9c', mr: 1 }}
                                  />
                                ) : null}
                                {params.InputProps.endAdornment}
                              </>
                            ),
                          }}
                          sx={fieldSx}
                        />
                      )}
                    />
                  )}

                  <Button
                    type="submit"
                    fullWidth
                    variant="contained"
                    disableElevation
                    disabled={loading}
                    sx={btnSx}
                  >
                    {loading ? (
                      <>
                        <CircularProgress
                          size={18}
                          thickness={5}
                          sx={{ color: '#04221d', mr: 1 }}
                        />
                        Sending…
                      </>
                    ) : (
                      'Submit'
                    )}
                  </Button>

                  <Box sx={{ textAlign: 'center', mt: 2 }}>
                    <Link
                      component="button"
                      type="button"
                      onClick={() => navigate('/login')}
                      sx={linkSx}
                    >
                      ‹ Back to login
                    </Link>
                  </Box>
                </Form>
              )}
            </Formik>
          </Box>
        </Paper>
      </Box>

      <Failed
        handleCloseModal={() => setShowFailed(false)}
        title="Email Id not found"
        isFailedModalOpen={showFailed}
      />

      {/* On-theme signing/success overlay — only on submit/success, not the email lookup */}
      <Backdrop
        open={loading || showSuccess}
        sx={{
          zIndex: theme => theme.zIndex.modal + 1,
          backdropFilter: 'blur(6px)',
          backgroundColor: 'rgba(3,12,22,0.72)',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 2,
            px: 6,
            py: 5,
            minWidth: 260,
            borderRadius: '20px',
            backgroundColor: '#0e1c2e',
            border: '1px solid #243652',
            boxShadow: '0 30px 80px -28px rgba(0,0,0,0.85)',
            fontFamily: "'Hanken Grotesk', system-ui, sans-serif",
          }}
        >
          {showSuccess ? (
            <>
              <Box
                component="svg"
                viewBox="0 0 52 52"
                sx={{ width: 72, height: 72 }}
              >
                <Box
                  component="circle"
                  cx="26"
                  cy="26"
                  r="24"
                  fill="none"
                  stroke="#2dd4bf"
                  strokeWidth="2.5"
                  sx={{
                    strokeDasharray: 151,
                    strokeDashoffset: 151,
                    animation: `${drawRing} 0.5s ease forwards`,
                  }}
                />
                <Box
                  component="path"
                  d="M16 27l7 7 13-14"
                  fill="none"
                  stroke="#2dd4bf"
                  strokeWidth="3"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  sx={{
                    strokeDasharray: 40,
                    strokeDashoffset: 40,
                    animation: `${drawCheck} 0.35s 0.4s ease forwards`,
                  }}
                />
              </Box>
              <Typography
                sx={{
                  fontFamily: "'Spectral',serif",
                  fontSize: 20,
                  fontWeight: 600,
                  color: '#e7eef7',
                }}
              >
                Code sent
              </Typography>
              <Typography sx={{ fontSize: 13, color: '#9fb2cc', mt: -1 }}>
                Check your email for the verification code
              </Typography>
            </>
          ) : (
            <>
              <CircularProgress
                size={48}
                thickness={4}
                sx={{ color: '#2dd4bf' }}
              />
              <Typography sx={{ fontSize: 14, color: '#9fb2cc' }}>Sending code…</Typography>
            </>
          )}
        </Box>
      </Backdrop>
    </>
  );
};

export default ForgotPasswordV2;
